import { OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type Controls<T> = { [K in keyof T]-?: AbstractControl };

export type ControlsNames<T> = { [K in keyof T]-?: K };

export type ControlMap<T, V> = { [K in keyof T]-?: V };

export type ControlsType<T> = {
    [K in keyof T]-?: T[K] extends any[] ? TypedFormArray<T[K]> : TypedFormControl<T[K]> | TypedFormGroup<T[K]>;
};

export type FormErrorsType<T> = {
    [K in keyof T]-?: T[K] extends any[] ? (null | ValidationErrors)[] : ValidationErrors;
};

export type FormUpdate<FormInterface> = { [FormControlInterface in keyof FormInterface]?: true };

export type FormErrors<FormInterface> = null | Partial<
    FormErrorsType<FormInterface> & {
        formGroup?: ValidationErrors;
    }
>;

// using set/patch value options signature from form controls to allow typing without additional casting
export interface TypedAbstractControl<TValue> extends AbstractControl {
    value: TValue;
    valueChanges: Observable<TValue>;
    setValue(value: TValue, options?: Parameters<AbstractControl['setValue']>[1]): void;
    patchValue(value: Partial<TValue>, options?: Parameters<AbstractControl['patchValue']>[1]): void;
}

export interface TypedFormGroup<TValue> extends UntypedFormGroup {
    value: TValue;
    valueChanges: Observable<TValue>;
    controls: ControlsType<TValue>;
    setValue(value: TValue, options?: Parameters<UntypedFormGroup['setValue']>[1]): void;
    patchValue(value: Partial<TValue>, options?: Parameters<UntypedFormGroup['patchValue']>[1]): void;
    getRawValue(): TValue;
}

export interface TypedFormArray<TValue extends any[]> extends UntypedFormArray {
    value: TValue;
    valueChanges: Observable<TValue>;
    controls: TypedAbstractControl<TValue[0]>[];
    setValue(value: TValue, options?: Parameters<UntypedFormArray['setValue']>[1]): void;
    patchValue(value: TValue, options?: Parameters<UntypedFormArray['patchValue']>[1]): void;
    getRawValue(): TValue;
}

export interface TypedFormControl<TValue> extends UntypedFormGroup {
    value: TValue;
    valueChanges: Observable<TValue>;
    setValue(value: TValue, options?: Parameters<UntypedFormControl['setValue']>[1]): void;
    patchValue(value: Partial<TValue>, options?: Parameters<UntypedFormControl['patchValue']>[1]): void;
}

export type KeysWithType<T, V> = { [K in keyof T]: T[K] extends V ? K : never }[keyof T];

export type ArrayPropertyKey<T> = KeysWithType<T, Array<any>>;

export type ArrayPropertyValue<T, K extends ArrayPropertyKey<T> = ArrayPropertyKey<T>> = T[K] extends Array<infer U> ? U : never;

const wrapAsQuote = (str: string): string => `"${str}"`;

export class MissingFormControlsError<T extends string> extends Error {
    constructor(missingFormControls: T[]) {
        super(
            `Attempt to update the form value with an object that doesn't contains some of the required form control keys.\nMissing: ${missingFormControls
                .map(wrapAsQuote)
                .join(`, `)}`
        );
    }
}

/**
 * Easily unsubscribe from an observable stream by appending `takeUntilDestroyed(this)` to the observable pipe.
 * If the component already has a `ngOnDestroy` method defined, it will call this first.
 * Note that the component *must* implement OnDestroy for this to work (the typings will enforce this anyway)
 */
export function takeUntilDestroyed<T>(component: OnDestroy): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>): Observable<T> => {
        const onDestroy = new Subject<boolean>();
        const previousOnDestroy = component.ngOnDestroy;

        component.ngOnDestroy = () => {
            if (previousOnDestroy) {
                previousOnDestroy.apply(component);
            }

            onDestroy.next(true);
            onDestroy.complete();
        };

        return source.pipe(takeUntil(onDestroy));
    };
}

/** @internal */
export function isNullOrUndefined(obj: any): obj is null | undefined {
    return obj === null || obj === undefined;
}
